/**
 * Public course detail + watch page at `/courses/:slug`.
 *
 * Three states:
 *   - Not signed in → sign-in prompt
 *   - Signed in, not paid → preview lessons + Buy button
 *   - Signed in, paid → full lesson player
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeftIcon as ArrowLeft, LockIcon as Lock, CheckCircleIcon as CheckCircle, GraduationCapIcon as GraduationCap, PlayCircleIcon as PlayCircle } from "@phosphor-icons/react";
import { isNativeApp } from "@/lib/nativeBridge";
import NativePlanNotice from "@/components/NativePlanNotice";

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
};
const $ = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

export default function CourseDetail() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [activeLesson, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/trainer-courses/by-slug/${slug}`)
      .then((r) => {
        setCourse(r.data);
        const firstWatchable = (r.data.lessons || []).find((l) => l.video_url);
        if (firstWatchable) setActive(firstWatchable);
      })
      .catch(() => setCourse({ notFound: true }));
  };
  useEffect(load, [slug]);

  const buy = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/trainer-courses/${course.id}/buy`);
      if (r.data.already_owned) { toast.success("You already own this — reloading."); load(); return; }
      window.location.href = r.data.checkout_url;
    } catch (e) {
      if (e?.response?.status === 401) {
        window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`;
      } else {
        toast.error(e?.response?.data?.detail || "Couldn't start checkout");
      }
    } finally { setBusy(false); }
  };

  if (!course) return <div className="min-h-screen grid place-items-center text-[#666]">Loading…</div>;
  if (course.notFound) return (
    <div className="min-h-screen bg-[#FAFAFA] grid place-items-center text-center px-6">
      <div>
        <h1 className="font-spectral text-3xl">Course not found</h1>
        <Link to="/courses" className="inline-flex items-center gap-1.5 mt-4 text-sm underline">
          <ArrowLeft size={14} /> Back to catalog
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] font-manrope text-ink">
      <Helmet>
        <title>{`${course.title} — Armstrong K9 Registry`}</title>
        <meta name="description" content={course.subtitle || course.description?.slice(0, 200) || ""} />
        {course.cover_url && <meta property="og:image" content={resolveUrl(course.cover_url)} />}
        {/* Schema.org/Course JSON-LD so Google can render rich result
            cards (provider, price, lesson count) directly in SERPs. */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Course",
          name: course.title,
          description: course.subtitle || course.description || "",
          provider: {
            "@type": "Organization",
            name: course.provider_name || "Armstrong K9 Registry",
          },
          offers: {
            "@type": "Offer",
            price: ((course.flat_price_cents || 0) / 100).toFixed(2),
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
          },
          ...(course.cover_url && { image: resolveUrl(course.cover_url) }),
        })}</script>
      </Helmet>

      <header className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3"><img src="/logo-256.png" alt="" className="h-8 w-8 rounded-full" /><span className="font-spectral">Armstrong K9 Registry</span></Link>
          <Link to="/courses" className="text-sm text-[#666] hover:text-ink">All courses</Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 md:px-10 py-10 grid lg:grid-cols-[1fr,320px] gap-8">
        <div className="min-w-0">
          {activeLesson?.video_url ? (
            <video
              src={resolveUrl(activeLesson.video_url)}
              controls
              playsInline
              className="w-full block rounded-2xl bg-black aspect-video"
              data-testid="course-lesson-player"
            />
          ) : (
            <div className="aspect-video rounded-2xl bg-black grid place-items-center text-white" data-testid="course-lesson-locked">
              <div className="text-center px-6">
                <Lock size={48} className="mx-auto text-gold mb-3" />
                <p className="font-spectral text-2xl">Locked</p>
                <p className="text-sm text-white/70 mt-2">Buy this course to unlock every lesson.</p>
              </div>
            </div>
          )}
          <p className="text-xs uppercase tracking-[0.18em] text-gold font-bold mt-6">By {course.provider_name}</p>
          <h1 className="font-spectral text-3xl sm:text-4xl mt-1">{course.title}</h1>
          {course.subtitle && <p className="text-base text-[#3F3F3F] mt-3">{course.subtitle}</p>}
          {course.description && <p className="text-sm text-[#3F3F3F] mt-4 whitespace-pre-wrap leading-relaxed">{course.description}</p>}
        </div>

        {/* Sidebar */}
        <aside className="bg-white border border-[#E5E5E5] rounded-2xl p-5 h-fit">
          {course.has_paid ? (
            <>
              <p className="inline-flex items-center gap-1.5 text-emerald-700 text-sm font-semibold"><CheckCircle size={14} weight="fill" /> You own this course</p>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/trainer-courses/by-slug/${course.slug}/certificate.pdf`}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-[12px] underline text-[#666] hover:text-ink"
                data-testid="course-cert-btn"
              >
                Download completion certificate (PDF)
              </a>
              <p className="text-[10.5px] text-[#999] mt-1">Available after you've watched every non-preview lesson ≥30s.</p>
            </>
          ) : (
            <>
              <p className="text-3xl font-spectral">{$(course.flat_price_cents)}</p>
              <p className="text-xs text-[#666] mt-1">One-time purchase · Lifetime access</p>
              <Button onClick={buy} disabled={busy} className="w-full mt-4" data-testid="course-buy-btn">
                {busy ? "Opening checkout…" : `Buy course for ${$(course.flat_price_cents)}`}
              </Button>
            </>
          )}
          <div className="mt-5 border-t border-[#EEE] pt-4">
            <p className="text-xs uppercase tracking-wider text-[#888] mb-2">Lessons</p>
            <ul className="space-y-1">
              {(course.lessons || []).map((l, i) => (
                <li key={l.id}>
                  <button
                    type="button"
                    onClick={() => l.video_url && setActive(l)}
                    disabled={!l.video_url}
                    className={"w-full text-left text-sm px-2 py-1.5 rounded-md flex items-center gap-2 " + (l.video_url ? "hover:bg-[#F4F4F4]" : "text-[#888] cursor-not-allowed") + (activeLesson?.id === l.id ? " bg-[#0E0E0E]/5 font-semibold" : "")}
                    data-testid={`course-lesson-${l.id}`}
                  >
                    {l.video_url ? <PlayCircle size={14} className="text-[#C1272D]" /> : <Lock size={14} />}
                    <span className="flex-1 truncate">{i + 1}. {l.title}</span>
                    {l.preview && <span className="text-[10px] text-emerald-700">Preview</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
}
